import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { Banner } from '../../../types/banner';
import { getCreativityForms, getTemplateByBannerType } from '../../../constants/clients/registry';
import { ConfigAccordion } from './config-display';
import { isVideoUrlField } from '../../../utils';
import { tokens } from '../../../theme/tokens';

dayjs.extend(relativeTime);
dayjs.locale('es');

interface ConfigurationCardProps {
  banner: Banner;
  clientId: string;
}

type FormField = {
  key: string;
  label: string;
  type: string;
  [key: string]: any;
};

const getFieldLabel = (key: string, bannerType: string, clientId: string, value?: any): string => {
  // Si el valor es un video, retornar "Source"
  if (value && isVideoUrlField(value)) {
    return 'Source';
  }

  // Labels custom para campos que ahora vienen a nivel banner
  if (key === 'suggested_copy') return 'Suggested copy';
  if (key === 'viral_score') return 'Viral score';

  const forms = getCreativityForms(clientId);
  const form = forms[bannerType];

  if (!form || !form.fields) return key;

  const field = (form.fields as FormField[]).find(f => f.key === key);
  return field?.label || key.charAt(0).toUpperCase() + key.slice(1);
};

const formatValue = (value: any) => {
  // Si es objeto VideoUrl
  if (isVideoUrlField(value)) {
    return (
      <a href={value.originalUrl} target='_blank' rel='noopener noreferrer' style={{ color: '#1890ff' }}>
        Link
      </a>
    );
  }

  // Si es string URL (imagen o cualquier archivo)
  if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
    return (
      <a href={value} target='_blank' rel='noopener noreferrer' style={{ color: '#1890ff' }}>
        Link
      </a>
    );
  }

  // Booleanos
  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }

  // Todo lo demás
  return String(value);
};

export const ConfigurationCard: React.FC<ConfigurationCardProps> = ({ banner, clientId }) => {
  const config = banner.creativityConfig;
  if (!config || typeof config !== 'object') return null;
  if (!banner.bannerType) return null;

  const bannerType = banner.bannerType;

  // config + campos a nivel banner
  const mergedData: Record<string, any> = {
    ...(config as Record<string, any>),
    ...(banner.suggested_copy ? { suggested_copy: banner.suggested_copy } : {}),
    ...(typeof banner.viral_score === 'number' ? { viral_score: banner.viral_score } : {})
  };

  const entries = Object.entries(mergedData).filter(
    ([, value]) => value !== undefined && value !== null && value !== ''
  );
  if (entries.length === 0) return null;

  const template = getTemplateByBannerType(bannerType);
  const accordionConfig = template?.config?.accordionConfig;

  // Helper para obtener valor de mergedData de forma type-safe
  const getConfigValue = (key: string) => (mergedData as Record<string, any>)[key];

  // Si no hay acordeón configurado, crear uno por defecto con todos los campos
  const finalAccordionConfig = accordionConfig || [
    {
      key: 'default',
      title: 'Detalle',
      subtitle: 'Configuración de la plantilla',
      fields: entries.map(([key]) => key),
      defaultOpen: true,
      color: tokens.colors.brands.blue[200]
    }
  ];

  return (
    <div
      style={{
        borderRadius: 16,
        display: 'contents'
      }}
    >
      <ConfigAccordion
        config={finalAccordionConfig}
        data={mergedData}
        getFieldLabel={(key: string) => getFieldLabel(key, bannerType, clientId, getConfigValue(key))}
        formatValue={formatValue}
      />
    </div>
  );
};
